import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Camera, Mic, ArrowUp, Check, Play, RotateCcw, ChevronRight, ChevronLeft } from 'lucide-react';
import { useDemoChat } from '../DemoChat';
import { DemoChatProvider } from '../DemoChat/DemoChatProvider';
import StreamingMessage from '../StreamingMessage';
import InteractiveCards from '../InteractiveCards';
import CardErrorBoundary from '../CardErrorBoundary';
import LoadingIndicator from '../LoadingIndicator';
import { usecases } from '../../data/usecases';

// Stable empty objects/arrays to avoid re-renders
const EMPTY_CARD_STATES = {};
const EMPTY_ARRAY = [];
const EMPTY_CROPPED_IMAGES = {};
const EMPTY_MATCH_STATUSES = {};

/**
 * PhoneFrame - Renders a minimal phone mockup with camera flash
 * Always renders in dark mode regardless of system preference
 */
const PhoneFrame = ({ children, className, showFlash, flashKey }) => (
  <div className={`dark relative bg-black rounded-[2rem] sm:rounded-[2.5rem] p-1 sm:p-1.5 shadow-2xl ${className || ''}`}>
    {/* Camera flash overlay - highest z-index to appear above everything */}
    {showFlash && (
      <div 
        key={`flash-${flashKey}`}
        className="absolute inset-0 bg-white/90 z-50 rounded-[1.8rem] sm:rounded-[2.3rem] pointer-events-none" 
        style={{ animation: 'flash 0.4s ease-out' }} 
      />
    )}
    
    {/* Screen - dark class inherited from parent forces dark mode */}
    <div className="relative bg-[#111111] rounded-[1.8rem] sm:rounded-[2.3rem] overflow-hidden">
      {/* Content */}
      {children}
      
      {/* Home indicator */}
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-24 sm:w-28 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
    </div>
    
    {/* Flash animation */}
    <style>{`
      @keyframes flash {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
    `}</style>
  </div>
);

/**
 * PhoneInputArea - Matches the real app's ChatSection input styling
 * Fixed height, content scrolls/expands within
 */
const PhoneInputArea = ({ playbackPhase, typingText }) => {
  const isTyping = playbackPhase === 'typing';
  const isCaptured = playbackPhase === 'captured';
  const hasContent = isTyping && typingText;
  
  return (
    <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-[#1a1a18] via-[#1a1a18] to-transparent pt-6 pb-3 px-2 sm:px-3">
      {/* Pill container - matches real app */}
      <div className="p-1 px-1.5 w-full border border-gray-600/30 rounded-3xl shadow bg-gray-700">
        <div className="flex items-center gap-2">
          {/* Camera button - green like real app */}
          <button className={`flex-shrink-0 p-2 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
            isCaptured
              ? 'bg-green-500 text-white scale-105'
              : 'bg-green-400/20 text-green-500 border border-green-500/10'
          }`}>
            {isCaptured ? <Check size={20} /> : <Camera size={20} />}
          </button>
          
          {/* Input area - expands vertically but within fixed bounds */}
          <div className="flex-1 min-w-0 flex items-center">
            <div className={`w-full py-3 px-1 text-sm leading-5 transition-all ${
              hasContent ? 'text-white' : 'text-gray-400'
            }`} style={{ minHeight: '20px', maxHeight: '120px' }}>
              {hasContent ? (
                <div className="flex items-end">
                  <span className="break-words whitespace-pre-wrap">{typingText}</span>
                  <span className="inline-block w-0.5 h-4 bg-blue-400 ml-0.5 flex-shrink-0 animate-pulse" />
                </div>
              ) : isCaptured ? (
                <span className="flex items-center gap-2 text-green-400">
                  <Check size={14} />
                  Photo captured!
                </span>
              ) : (
                <span>Ask anything</span>
              )}
            </div>
          </div>
          
          {/* Mic button */}
          <button className="flex-shrink-0 p-2 w-10 h-10 rounded-full flex items-center justify-center bg-gray-700 text-gray-300 hover:bg-gray-600">
            <Mic size={18} />
          </button>
          
          {/* Send button - gray like real app */}
          <button className="flex-shrink-0 p-2 w-10 h-10 rounded-full flex items-center justify-center bg-gray-600 text-white">
            <ArrowUp size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * CameraViewfinder - Fake camera overlay that appears when taking photo
 */
const CameraViewfinder = ({ imageUrl, showFlash }) => (
  <div className="absolute inset-0 z-30 bg-black flex flex-col">
    {/* Viewfinder image - fills most of the screen */}
    <div className="flex-1 relative overflow-hidden">
      <img 
        src={imageUrl} 
        alt="Camera viewfinder" 
        className="w-full h-full object-cover"
      />
      {/* Viewfinder grid overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="w-full h-full grid grid-cols-3 grid-rows-3">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="border border-white/20" />
          ))}
        </div>
      </div>
      {/* Focus indicator in center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-16 h-16 border-2 border-yellow-400 rounded-lg animate-pulse" />
      </div>
    </div>
    
    {/* Camera controls at bottom */}
    <div className="bg-black/90 py-6 flex items-center justify-center">
      {/* Shutter button - large white circle with inner ring */}
      <div className={`relative w-16 h-16 rounded-full transition-all duration-200 ${
        showFlash ? 'scale-90 bg-gray-400' : 'bg-white'
      }`}>
        <div className="absolute inset-1 rounded-full border-2 border-gray-800" />
      </div>
    </div>
  </div>
);

/**
 * DemoOverlay - Start/Restart overlay for the phone demo
 * Start state: Clean centered button, entire area clickable
 * Complete state: Centered completion message
 */
const DemoOverlay = ({ type, onAction, usecase }) => {
  const isStart = type === 'start';
  
  if (isStart) {
    // Entire overlay is clickable to start, with centered Start button
    return (
      <div 
        onClick={onAction}
        className="absolute inset-0 z-40 flex items-center justify-center rounded-[1.8rem] sm:rounded-[2.3rem] cursor-pointer"
      >
        <button
          className="group flex items-center gap-3 px-6 py-3 bg-white text-gray-900 rounded-full font-bold shadow-2xl hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 pointer-events-none"
        >
          <Play size={20} className="fill-current" />
          <span>Start</span>
        </button>
      </div>
    );
  }
  
  // Completion state
  return (
    <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/80 rounded-[1.8rem] sm:rounded-[2.3rem]">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
        <Check size={28} className="text-emerald-400" />
      </div>
      <p className="text-white/60 text-sm mb-4">Demo complete!</p>
      <button
        onClick={onAction}
        className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-all"
      >
        <RotateCcw size={16} />
        <span>Restart</span>
      </button>
    </div>
  );
};

/**
 * ChapterNarrative - Shows chapter navigation
 * Desktop: Unified panel with title, description, CTA, and chapters
 * Mobile: Current chapter with back/forth navigation
 */
const ChapterNarrative = ({ 
  chapters = [], 
  chapterIndex, 
  onContinue, 
  onJumpToChapter,
  isComplete, 
  isPaused,
  hasStarted = false, // Whether demo has started (show expanded chapter only when true)
  usecase = null // For desktop unified panel
}) => {
  const totalChapters = chapters.length;
  const currentChapter = chapters[chapterIndex];
  
  if (!chapters.length && !isComplete) return null;
  
  const showContinueButton = isPaused || isComplete;
  const isPlaying = !isPaused && !isComplete;
  
  return (
    <>
      {/* Desktop: Unified panel with intro + chapters */}
      <div className="hidden lg:flex flex-col gap-4 items-start w-96">
        {/* Title - always visible */}
        {usecase && (
          <div className="w-full">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1">
              {usecase.title}
            </h3>
          </div>
        )}
        
        {/* Description - only before demo starts */}
        {!hasStarted && usecase && (
          <div className="w-full animate-chapter-card-in">
            <p className="text-lg text-white/60 leading-relaxed">
              {usecase.description}
            </p>
          </div>
        )}
        
        {/* Chapter list */}
        <div className="flex flex-col gap-2 items-start w-full mt-4">
          {chapters.map((chapter, idx) => {
            const isActiveChapter = idx === chapterIndex && hasStarted;
            const isCompleted = idx < chapterIndex && hasStarted;
            
            return !isActiveChapter ? (
              /* Inactive/collapsed chapter - pill shape, width fits content */
              <button
                key={chapter.id || idx}
                onClick={() => onJumpToChapter?.(idx)}
                className="flex items-center gap-2 px-4 py-3 rounded-full bg-gray-800/80 hover:bg-gray-700 transition-colors duration-150 animate-chapter-pill-in"
              >
                {/* Status indicator */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold transition-colors duration-150 ${
                  isCompleted ? 'bg-emerald-500 text-white' : 'bg-gray-700 text-white/50'
                }`}>
                  {isCompleted ? '✓' : idx + 1}
                </div>
                
                {/* Chapter title */}
                <span className={`text-base font-medium transition-colors duration-150 ${
                  isCompleted ? 'text-white/70' : 'text-white/80'
                }`}>
                  {chapter.title || `Part ${idx + 1}`}
                </span>
              </button>
            ) : (
              /* Active chapter - rounded box, full width */
              <div 
                key={chapter.id || idx} 
                className="w-full rounded-2xl bg-gray-800 p-5 animate-chapter-card-in"
              >
                {/* Narrative text with title */}
                <p className="text-white text-base leading-relaxed animate-chapter-content-in">
                  <span className="font-bold">{chapter.title || `Part ${idx + 1}`}:</span>{' '}
                  {chapter.narrative}
                </p>

                {/* Playing indicator */}
                {isPlaying && (
                  <div className="flex items-center gap-2 mt-3 animate-chapter-content-in" style={{ animationDelay: '50ms' }}>
                    <div className="flex gap-0.5">
                      <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse-dot" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse-dot" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                
                {showContinueButton && (
                  <button
                    onClick={onContinue}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-100 transition-all animate-chapter-content-in"
                    style={{ animationDelay: '100ms' }}
                  >
                    {isComplete ? (
                      <>
                        <RotateCcw size={14} />
                        <span>Restart</span>
                      </>
                    ) : (
                      <>
                        <span>Continue</span>
                        <ChevronRight size={14} className="animate-bounce-x" />
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Completion state - shows when all done */}
          {isComplete && (
            <div className="w-full px-5 py-4 bg-emerald-500/20 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
                <span className="text-emerald-400 text-sm font-medium">Complete</span>
              </div>
              <button
                onClick={onContinue}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-100 transition-all"
              >
                <RotateCcw size={14} />
                <span>Restart</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile: Compact view with current chapter + nav */}
      <div className="lg:hidden bg-gray-800 rounded-2xl p-4">
        {/* Chapter indicator dots + position */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1">
            {Array.from({ length: totalChapters }).map((_, i) => (
              <button
                key={i}
                onClick={() => onJumpToChapter?.(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i <= chapterIndex ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
          <span className="text-white/50 text-xs">
            {isComplete ? 'Complete' : `${chapterIndex + 1} of ${totalChapters}`}
          </span>
        </div>
        
        {/* Current chapter narrative */}
        <p className="text-white text-sm leading-relaxed mb-4">
          {isComplete ? 'Session complete.' : currentChapter?.narrative}
        </p>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          {/* Back button */}
          {chapterIndex > 0 && !isComplete && (
            <button
              onClick={() => onJumpToChapter?.(chapterIndex - 1)}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 transition-all"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          
          {/* Main action */}
          <button
            onClick={onContinue}
            className={`relative flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all overflow-hidden
              ${showContinueButton || isComplete
                ? 'bg-white text-gray-900 hover:bg-gray-100 animate-pulse-subtle shadow-[0_0_0_0_rgba(255,255,255,0.4)]'
                : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            style={showContinueButton || isComplete ? {
              animation: 'ctaPulse 1.8s ease-in-out infinite'
            } : {}}
          >
            {isComplete ? (
              <>
                <RotateCcw size={16} />
                <span>Restart</span>
              </>
            ) : showContinueButton ? (
              <>
                <span>Continue</span>
                <ChevronRight size={16} />
              </>
            ) : (
              <>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse-dot" />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse-dot" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse-dot" style={{ animationDelay: '300ms' }} />
                </div>
                <span>Watching</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes ctaPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(255, 255, 255, 0); }
        }
        @keyframes narrative-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
          50% { box-shadow: 0 0 0 6px rgba(255, 255, 255, 0); }
        }
        .animate-narrative-pulse {
          animation: narrative-pulse 2s ease-in-out infinite;
        }
        @keyframes bounce-x {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(3px); }
        }
        .animate-bounce-x {
          animation: bounce-x 1s ease-in-out infinite;
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
        .animate-pulse-dot {
          animation: pulse-dot 1.2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

/**
 * PhoneDemo - The phone with demo content inside
 * Must be used inside DemoChatProvider
 */
const PhoneDemo = ({ usecase, showOverlay, overlayType, onOverlayAction, intentTypingText, completedIntent, isPreview = false }) => {
  const demo = useDemoChat();
  const showViewfinder = !!demo?.cameraViewfinderImage;
  
  // Show intent typing text if provided, otherwise show demo typing text
  const displayTypingText = intentTypingText !== null ? intentTypingText : demo?.typingText;
  const isTypingIntent = intentTypingText !== null;
  
  return (
    <div>
      <PhoneFrame showFlash={demo?.showCameraFlash} flashKey={demo?.flashKey}>
        {/* Content container - fixed height with input overlay */}
        <div className="relative h-[540px] sm:h-[620px] lg:h-[700px]">
          {/* Chat area - renders messages directly using context */}
          <DemoChatRenderer usecase={usecase} completedIntent={completedIntent} isPreview={isPreview} />
          
          {/* Input area - absolutely positioned at bottom */}
          <PhoneInputArea 
            playbackPhase={isTypingIntent ? 'typing' : demo?.playbackPhase} 
            typingText={displayTypingText}
          />
          
          {/* Camera viewfinder overlay - appears when taking photo */}
          {showViewfinder && (
            <CameraViewfinder 
              imageUrl={demo.cameraViewfinderImage} 
              showFlash={demo?.showCameraFlash}
            />
          )}
          
          {/* Start/Restart overlay */}
          {showOverlay && (
            <DemoOverlay 
              type={overlayType} 
              onAction={onOverlayAction}
              usecase={usecase}
            />
          )}
        </div>
      </PhoneFrame>
      
      {/* Progress indicator only - title shown in selector */}
      {/* {demo && (
        <div className="flex items-center justify-center mt-4 px-2">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: demo.totalMessages }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i < demo.visibleMessageCount 
                    ? 'bg-white' 
                    : 'bg-white/30'
                }`}
              />
            ))}
            <span className="text-white/40 text-xs ml-2">
              {usecase?.duration_reading}
            </span>
          </div>
        </div>
      )} */}
    </div>
  );
};

/**
 * DemoChatRenderer - Renders messages from context using real app components
 */
const DemoChatRenderer = ({ usecase, completedIntent = null, isPreview = false }) => {
  const demo = useDemoChat();
  const scrollRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const lastExpandedIndexRef = useRef(null);
  
  // Helper to scroll to bottom
  const scrollToBottom = useCallback((delay = 100) => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, delay);
  }, []);
  
  // Helper to slowly scroll to bottom (for observe card button reveal)
  const scrollToBottomSlow = useCallback((delay = 100) => {
    setTimeout(() => {
      if (scrollRef.current) {
        // Use a longer duration by scrolling in steps
        const container = scrollRef.current;
        const targetScroll = container.scrollHeight;
        const startScroll = container.scrollTop;
        const distance = targetScroll - startScroll;
        const duration = 1800; // 1800ms for slow scroll - gives time to read
        const startTime = performance.now();
        
        const animateScroll = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Ease-in-out function for natural feel
          const easeInOut = progress < 0.5 
            ? 2 * progress * progress 
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
          container.scrollTop = startScroll + (distance * easeInOut);
          
          if (progress < 1) {
            requestAnimationFrame(animateScroll);
          }
        };
        
        requestAnimationFrame(animateScroll);
      }
    }, delay);
  }, []);
  
  // Helper to scroll expanded card to TOP of view (so image is visible)
  const scrollCardToTop = useCallback((messageId, delay = 100) => {
    setTimeout(() => {
      if (scrollRef.current && messageId) {
        // Find the cards container directly using our data attribute
        const cardsContainer = scrollRef.current.querySelector(`[data-cards-container="${messageId}"]`);
        if (!cardsContainer) {
          console.log('scrollCardToTop: cards container not found for', messageId);
          return;
        }
        
        const containerRect = scrollRef.current.getBoundingClientRect();
        const cardRect = cardsContainer.getBoundingClientRect();
        
        // Calculate scroll position to align CARD top with container top (small padding)
        const scrollTop = scrollRef.current.scrollTop + (cardRect.top - containerRect.top) - 12;
        
        scrollRef.current.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'auto' // Fast/instant scroll to top
        });
      }
    }, delay);
  }, []);
  
  // Auto-scroll when messages change - with delay to let content render
  useEffect(() => {
    const messageCount = demo?.visibleMessages?.length || 0;
    if (scrollRef.current && messageCount > lastMessageCountRef.current) {
      scrollToBottom(100);
    }
    lastMessageCountRef.current = messageCount;
  }, [demo?.visibleMessages?.length, scrollToBottom]);

  // Auto-expand first expandable card after a new message appears.
  // Searches backward from the newest message to find the last assistant message
  // with expandable cards. This keeps cards expanded when a chapter ends on a
  // user message (e.g. form submission) — the previous assistant card stays open.
  // The 600ms timeout naturally debounces during fast playback: only the final
  // expansion fires (intermediate ones are cancelled by the cleanup).
  useEffect(() => {
    if (!demo) return;
    const messages = demo.visibleMessages || [];

    // Walk backward to find the last assistant message with expandable cards
    let targetMessage = null;
    let targetIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant') {
        const cards = messages[i].message_metadata?.interactive_cards || [];
        if (cards.length > 0) {
          targetMessage = messages[i];
          targetIndex = i;
          break;
        }
      }
    }

    if (targetMessage) {
      const cards = targetMessage.message_metadata?.interactive_cards || [];
      const hasFollowupUserMessage = messages
        .slice(targetIndex + 1)
        .some(m => m?.role === 'user');
      const cardToExpandIndex = cards.findIndex(c => {
        const isExpandableType =
          c.type === 'spotlight' || c.type === 'observe_photo' || c.type === 'observe' ||
          c.type === 'location' || c.type === 'learn' ||
          c.type === 'observe_form' || c.type === 'plan_update';
        if (!isExpandableType) return false;
        // Don't re-expand forms after they were submitted (user already responded)
        if (hasFollowupUserMessage && c.type === 'observe_form') return false;
        return true;
      });

      if (cardToExpandIndex >= 0) {
        const expandTimer = setTimeout(() => {
          demo.setDemoExpandedCardIndex?.(cardToExpandIndex);
          demo.setDemoExpandedMessageId?.(targetMessage.id);
        }, 600);

        return () => {
          clearTimeout(expandTimer);
        };
      }
    }
  }, [demo?.visibleMessages?.length]);

  // Scroll to bottom when cards expand (with delay for animation to complete)
  // BUT only for non-photo cards (photo cards use 'viewing' phase and scroll separately)
  useEffect(() => {
    if (!demo) return;
    
    const currentExpanded = demo.demoExpandedCardIndex;
    const phase = demo.playbackPhase;
    
    // Only scroll when expansion changes AND we're not in 'viewing' phase
    // (viewing phase means we want to stay at top to see the image/instructions)
    if (currentExpanded !== null && currentExpanded !== lastExpandedIndexRef.current && phase !== 'viewing') {
      // Wait for expansion animation to complete, then scroll.
      // First scroll catches most of the content; second scroll (after a longer
      // delay) catches cards whose markdown/content finishes rendering late
      // (e.g. learn cards with long show_more_content).
      scrollToBottom(400);
      scrollToBottom(900);
    }
    
    lastExpandedIndexRef.current = currentExpanded;
  }, [demo?.demoExpandedCardIndex, demo?.playbackPhase, scrollToBottom]);

  // When entering 'viewing' phase - scroll card to TOP so image is visible
  useEffect(() => {
    if (demo?.playbackPhase === 'viewing' && demo?.demoExpandedMessageId) {
      scrollCardToTop(demo.demoExpandedMessageId, 150); // Wait for card to expand, then scroll to top
    }
  }, [demo?.playbackPhase, demo?.demoExpandedMessageId, scrollCardToTop]);

  // Scroll to bottom when entering 'scrollingToButton' phase (slower scroll to Take Photo button)
  useEffect(() => {
    if (demo?.playbackPhase === 'scrollingToButton') {
      scrollToBottomSlow(100); // Slow scroll to bottom
    }
  }, [demo?.playbackPhase, scrollToBottomSlow]);
  
  // Scroll to bottom when entering 'scrolling' phase (for form cards etc.)
  useEffect(() => {
    if (demo?.playbackPhase === 'scrolling') {
      scrollToBottom(100);
    }
  }, [demo?.playbackPhase, scrollToBottom]);

  if (!demo) return null;

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto px-3 sm:px-4 pb-24 chat-container" style={{ willChange: 'scroll-position' }}>
      {/* User's initial intent - larger in preview mode, normal after demo starts */}
      {completedIntent && (
        <div className={`card-base-user animate-fadeIn ${isPreview ? 'pt-8' : 'pt-3'}`}>
          <div className="flex justify-end">
            <div className={`bg-gray-200 dark:bg-gray-600/40 text-gray-900 dark:text-gray-100 rounded-2xl ${
              isPreview 
                ? 'max-w-[95%] px-6 py-5' 
                : 'max-w-[75%] px-3.5 py-2'
            }`}>
              <p className={`whitespace-pre-wrap break-words ${
                isPreview ? 'text-xl sm:text-2xl font-medium leading-relaxed' : 'text-sm'
              }`}>
                {completedIntent}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat messages */}
      {demo.visibleMessages.map((message, msgIndex) => {
        const cards = message.message_metadata?.interactive_cards || [];
        const isLastMessage = msgIndex === demo.visibleMessages.length - 1;
        // Check both locations for imageUrl
        const userImageUrl = message.role === 'user' 
          ? (message.message_metadata?.imageUrl || message.imageUrl) 
          : null;
        
        return (
          <div 
            key={message.id}
            data-message-id={message.id}
            className={`${message.role === 'user' ? 'card-base-user' : 'card-base-agent'} animate-fadeIn`}
          >
            {message.role === 'user' ? (
              /* Custom user message rendering - image INSIDE bubble like real app */
              <div className="flex justify-end">
                <div className="max-w-[75%] bg-gray-200 dark:bg-gray-600/40 text-gray-900 dark:text-gray-100 px-3.5 py-2 rounded-2xl">
                  {/* Image inside bubble, above text */}
                  {userImageUrl && (
                    <div className="mb-1">
                      <img 
                        src={userImageUrl}
                        alt="User shared"
                        className="max-w-full w-auto h-auto max-h-48 rounded-xl mt-1 border border-gray-300 dark:border-gray-600 object-cover"
                      />
                    </div>
                  )}
                  {/* User text */}
                  <div className="text-[14px] leading-relaxed">
                    {message.content}
                  </div>
                </div>
              </div>
            ) : (
              /* Assistant messages use StreamingMessage */
              <StreamingMessage message={message} />
            )}
            
            {message.role === 'assistant' && cards.length > 0 && (
              <div data-cards-container={message.id}>
              <CardErrorBoundary>
                <InteractiveCards
                  cards={cards}
                  messageId={message.id}
                  isReadOnly={true}
                  handledCardIds={demo.demoHandledCardIds ?? EMPTY_ARRAY}
                  cardStates={message.message_metadata?.card_states || EMPTY_CARD_STATES}
                  croppedImages={EMPTY_CROPPED_IMAGES}
                  sourceImageUrl={message.message_metadata?.sourceImageUrl}
                  demoExpandedIndex={message.id === demo.demoExpandedMessageId ? demo.demoExpandedCardIndex : null}
                  demoClickingCardId={demo.demoClickingCardId}
                  demoConfirmedMatchIds={demo.demoConfirmedMatchIds ?? EMPTY_ARRAY}
                  objectiveMatchStatuses={EMPTY_MATCH_STATUSES}
                  demoMode={true}
                />
              </CardErrorBoundary>
              </div>
            )}
          </div>
        );
      })}
      
      {/* Thinking indicator - shown before AI responds */}
      {demo.showThinkingIndicator && (
        <div className="card-base-agent animate-fadeIn">
          <LoadingIndicator className="!mt-0 !py-2 !px-0" />
        </div>
      )}
      
      {/* Completion indicator */}
      {demo.isComplete && usecase?.outcome && (
        <div className="text-center py-6 mt-4 animate-fadeIn">
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl border border-emerald-200 dark:border-emerald-800">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              {usecase.outcome}
            </span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

/**
 * PhoneDemoWithOverlay - Wrapper that connects overlay state to demo context
 * Handles chapter-based pausing and narrative display
 * Must be used inside DemoChatProvider
 */
const PhoneDemoWithOverlay = ({ usecase, usecaseId, onChapterState }) => {
  const demo = useDemoChat();
  const [showStartOverlay, setShowStartOverlay] = useState(true);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [isAtChapterPause, setIsAtChapterPause] = useState(false);
  const [isAllComplete, setIsAllComplete] = useState(false);
  const [isTypingIntent, setIsTypingIntent] = useState(false);
  const [intentTypingText, setIntentTypingText] = useState('');
  const [intentComplete, setIntentComplete] = useState(false); // Track when intent typing finished
  const prevUsecaseIdRef = useRef(usecaseId);
  const hasStartedRef = useRef(false);
  
  // Get chapters and initial intent from usecase
  const chapters = useMemo(() => usecase?.chapters || [], [usecase?.chapters]);
  const hasChapters = chapters.length > 0;
  const initialIntent = usecase?.initial_intent || null;
  
  // Build a map of message_id -> chapter end index
  const chapterEndMap = useMemo(() => {
    if (!hasChapters || !usecase?.messages) return {};
    const map = {};
    chapters.forEach((ch, idx) => {
      const msgIndex = usecase.messages.findIndex(m => m.id === ch.end_at_message_id);
      if (msgIndex >= 0) {
        map[msgIndex] = idx;
      }
    });
    return map;
  }, [chapters, usecase?.messages, hasChapters]);
  
  // Reset when use case changes
  useEffect(() => {
    if (prevUsecaseIdRef.current !== usecaseId) {
      prevUsecaseIdRef.current = usecaseId;
      setShowStartOverlay(true);
      setCurrentChapterIndex(0);
      setIsAtChapterPause(false);
      setIsAllComplete(false);
      setIsTypingIntent(false);
      setIntentTypingText('');
      setIntentComplete(false);
      hasStartedRef.current = false;
    }
  }, [usecaseId]);
  
  // Watch for chapter boundaries
  const visibleCount = demo?.visibleMessageCount || 0;
  const totalMessages = demo?.totalMessages || 0;
  useEffect(() => {
    if (!hasStartedRef.current || showStartOverlay || !hasChapters) return;
    
    // Check if we just hit a chapter end
    const chapterEndIndex = chapterEndMap[visibleCount - 1];
    if (chapterEndIndex !== undefined && chapterEndIndex === currentChapterIndex && !isAtChapterPause) {
      // Pause at chapter boundary
      demo?.pause?.();
      setIsAtChapterPause(true);
    }
  }, [visibleCount, chapterEndMap, currentChapterIndex, isAtChapterPause, showStartOverlay, hasChapters, demo]);
  
  // Watch for overall completion (for usecases without chapters, or after last chapter)
  useEffect(() => {
    if (!hasStartedRef.current || showStartOverlay) return;
    
    const isComplete = visibleCount >= totalMessages && totalMessages > 0;
    if (isComplete && !isAllComplete && !isAtChapterPause) {
      const timer = setTimeout(() => {
        demo?.pause?.();
        setIsAllComplete(true);
        setIsAtChapterPause(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [visibleCount, totalMessages, isAllComplete, isAtChapterPause, showStartOverlay, demo]);
  
  // Handle start button - begins with intent typing if available
  const handleStart = useCallback(() => {
    setShowStartOverlay(false);
    hasStartedRef.current = true;
    
    if (initialIntent) {
      // Start typing the initial intent
      setIsTypingIntent(true);
      setIntentTypingText('');
      
      // Simulate typing animation
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex < initialIntent.length) {
          setIntentTypingText(initialIntent.slice(0, charIndex + 1));
          charIndex++;
        } else {
          clearInterval(typeInterval);
          // Pause after typing completes, then show as message and start demo
          setTimeout(() => {
            setIsTypingIntent(false);
            setIntentTypingText('');
            setIntentComplete(true); // Intent now shows as message in chat
            demo?.play?.();
          }, 800);
        }
      }, 40); // ~40ms per character for natural typing speed
    } else {
      // No initial intent, start demo immediately
      demo?.play?.();
    }
  }, [demo, initialIntent]);
  
  // Handle continue - stable ref to avoid re-renders
  const handleContinueRef = useRef(null);
  handleContinueRef.current = useCallback(() => {
    if (isAllComplete) {
      // Restart - go back to intent typing if available
      setCurrentChapterIndex(0);
      setIsAtChapterPause(false);
      setIsAllComplete(false);
      demo?.reset?.();
      
      if (initialIntent) {
        // Restart with intent typing
        setIsTypingIntent(true);
        setIntentTypingText('');
        setIntentComplete(false); // Hide the message while re-typing
        let charIndex = 0;
        const typeInterval = setInterval(() => {
          if (charIndex < initialIntent.length) {
            setIntentTypingText(initialIntent.slice(0, charIndex + 1));
            charIndex++;
          } else {
            clearInterval(typeInterval);
            setTimeout(() => {
              setIsTypingIntent(false);
              setIntentTypingText('');
              setIntentComplete(true);
              demo?.play?.();
            }, 800);
          }
        }, 40);
      } else {
        setTimeout(() => demo?.play?.(), 50);
      }
    } else {
      // Continue to next chapter
      setCurrentChapterIndex(prev => prev + 1);
      setIsAtChapterPause(false);
      demo?.play?.();
    }
  }, [isAllComplete, demo, initialIntent]);
  
  // Stable callback wrapper
  const handleContinue = useCallback(() => {
    handleContinueRef.current?.();
  }, []);
  
  // Ref for stable handleJumpToChapter (avoids recreating callback on every state change)
  const handleJumpToChapterRef = useRef(null);
  
  // Update the ref with current implementation
  handleJumpToChapterRef.current = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= chapters.length) return;
    
    const hasStarted = hasStartedRef.current && !showStartOverlay;
    
    // If clicking current chapter while demo is running
    if (targetIndex === currentChapterIndex && hasStarted) {
      if (isAtChapterPause) {
        handleContinue();
      }
      return;
    }
    
    // Find the end message index of the PREVIOUS chapter (to show all messages up to that point)
    // For chapter 0, we start from beginning (show 0 messages, then play)
    // For chapter N, we show all messages up to and including chapter N-1's end, then play from there
    let showUpToMessageIndex = -1; // -1 means show nothing initially
    if (targetIndex > 0) {
      const prevChapter = chapters[targetIndex - 1];
      const prevEndIndex = usecase?.messages?.findIndex(m => m.id === prevChapter.end_at_message_id);
      if (prevEndIndex >= 0) {
        // Show all messages up to and including the end of previous chapter
        showUpToMessageIndex = prevEndIndex;
      }
    }
    
    // Hide overlay and mark as started
    setShowStartOverlay(false);
    hasStartedRef.current = true;
    setIntentComplete(true); // Skip intent typing animation
    
    // Stop current playback
    demo?.pause?.();
    
    // Set the chapter state
    setCurrentChapterIndex(targetIndex);
    setIsAtChapterPause(false);
    setIsAllComplete(false);
    
    // Instantly show all messages up to start of target chapter using goToMessage
    // goToMessage(index) shows messages 0 through index (inclusive)
    if (showUpToMessageIndex >= 0) {
      demo?.goToMessage?.(showUpToMessageIndex);
    } else {
      // For chapter 0, reset to show no messages
      demo?.reset?.();
    }
    
    // Start playing from that point
    setTimeout(() => {
      demo?.play?.();
    }, 150);
  };
  
  // Stable callback that uses ref
  const handleJumpToChapter = useCallback((targetIndex) => {
    handleJumpToChapterRef.current?.(targetIndex);
  }, []);
  
  // isActive = demo has started (user clicked Watch Demo), including during intent typing
  // Use ref to track this to avoid triggering useEffect loops
  const isActive = hasStartedRef.current && !showStartOverlay;
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  useEffect(() => {
    const currentChapter = hasChapters ? chapters[currentChapterIndex] : null;
    const isActiveNow = hasStartedRef.current && !showStartOverlay;
    onChapterState?.({
      isAtPause: isAtChapterPause,
      isComplete: isAllComplete,
      isActive: isActiveNow,
      chapter: currentChapter,
      chapterIndex: currentChapterIndex,
      totalChapters: chapters.length,
      chapters, // Pass all chapters for accordion view
      hasChapters,
      onContinue: handleContinue,
      onJumpToChapter: handleJumpToChapter // Allow jumping to specific chapters
    });
  }, [isAtChapterPause, isAllComplete, currentChapterIndex, chapters, hasChapters, onChapterState, handleContinue, handleJumpToChapter, showStartOverlay]);
  
  // Show intent immediately when overlay is shown (so user sees it through gradient)
  // or after typing animation completes during playback
  const showIntent = showStartOverlay || intentComplete;
  
  return (
    <PhoneDemo 
      usecase={usecase}
      showOverlay={showStartOverlay}
      overlayType="start"
      onOverlayAction={handleStart}
      intentTypingText={isTypingIntent ? intentTypingText : null}
      completedIntent={showIntent ? initialIntent : null}
      isPreview={showStartOverlay}
    />
  );
};

// Use case data for grid display
const useCaseDisplayData = {
  'heat-pump': {
    title: 'Equipment Support',
    subtitle: 'For Manufacturers',
    benefit: 'Reduce support tickets',
    thumbnail: '/home/tile-equipment-support.jpg',
    color: 'from-blue-500/20 to-blue-600/5',
    borderColor: 'border-blue-500/30',
    hoverBorder: 'hover:border-blue-400/50'
  },
  'nature-real': {
    title: 'Visitor Experience',
    subtitle: 'For Tourism & Parks',
    benefit: 'Enhance engagement',
    thumbnail: '/home/tile-visitor-experience.jpg',
    color: 'from-emerald-500/20 to-emerald-600/5',
    borderColor: 'border-emerald-500/30',
    hoverBorder: 'hover:border-emerald-400/50'
  },
  'training': {
    title: 'Field Training',
    subtitle: 'For Workforce',
    benefit: 'Onboard faster',
    thumbnail: '/home/tile-field-training.jpg',
    color: 'from-amber-500/20 to-amber-600/5',
    borderColor: 'border-amber-500/30',
    hoverBorder: 'hover:border-amber-400/50'
  },
  'diy-home': {
    title: 'Home Projects',
    subtitle: 'For Retailers',
    benefit: 'Guide customers',
    thumbnail: '/home/tile-home-projects.jpg',
    color: 'from-rose-500/20 to-rose-600/5',
    borderColor: 'border-rose-500/30',
    hoverBorder: 'hover:border-rose-400/50'
  }
};

/**
 * UseCaseShowcase - Main hero section with interactive demo
 */
const UseCaseShowcase = () => {
  const [activeUsecaseId, setActiveUsecaseId] = useState(usecases[0]?.id);
  const [showGrid, setShowGrid] = useState(true); // Start with grid view
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [chapterState, setChapterState] = useState({
    isAtPause: false,
    isComplete: false,
    isActive: false,
    chapter: null,
    chapterIndex: 0,
    totalChapters: 0,
    chapters: [],
    hasChapters: false,
    onContinue: null,
    onJumpToChapter: null
  });

  const activeUsecase = usecases.find(u => u.id === activeUsecaseId) || usecases[0];

  // Preload background image
  useEffect(() => {
    if (!activeUsecase?.background_image) return;
    setBackgroundLoaded(false);
    const img = new Image();
    img.src = activeUsecase.background_image;
    img.onload = () => setBackgroundLoaded(true);
  }, [activeUsecase?.background_image]);

  const handleSelectUsecase = useCallback((id) => {
    setActiveUsecaseId(id);
    setShowGrid(false); // Switch to demo mode
  }, []);

  const handleBackToGrid = useCallback(() => {
    setShowGrid(true);
  }, []);
  
  const handleChapterState = useCallback((state) => {
    // Handle both object updates and function updates
    if (typeof state === 'function') {
      setChapterState(prev => ({ ...prev, ...state(prev) }));
    } else {
      setChapterState(prev => ({ ...prev, ...state }));
    }
  }, []);

  // Show chapters panel whenever the usecase has chapters (even before demo starts)
  const showChapters = activeUsecase?.chapters?.length > 0;

  return (
    <div>
      {showGrid ? (
        /* Grid selection mode - contained within parent layout */
        <div className="py-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <h2 className="text-xl lg:text-2xl font-semibold text-white mb-1">
              See it in action.
            </h2>
            <p className="text-sm lg:text-base text-white/50">
              Choose a use case to watch the demo.
            </p>
          </div>

          {/* Mobile: 2-column grid showing all use cases */}
          <div className="md:hidden">
            <div className="grid grid-cols-2 gap-3">
              {usecases.map((usecase) => {
                const displayData = useCaseDisplayData[usecase.id] || {
                  title: usecase.title,
                  subtitle: usecase.persona_label || '',
                  benefit: ''
                };

                return (
                  <button
                    key={usecase.id}
                    onClick={() => handleSelectUsecase(usecase.id)}
                    className="group text-left"
                  >
                    {/* Image - IKEA style thumbnail */}
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-2">
                      {displayData.thumbnail ? (
                        <img 
                          src={displayData.thumbnail} 
                          alt={displayData.title}
                          className="w-full h-full object-cover"
                        />
                      ) : usecase.background_image ? (
                        <img 
                          src={usecase.background_image} 
                          alt={displayData.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-emerald-600/30 to-emerald-900/30" />
                      )}
                      {/* Play icon */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-lg">
                          <Play size={18} className="fill-gray-900 text-gray-900 ml-0.5" />
                        </div>
                      </div>
                    </div>

                    {/* Title only - compact */}
                    <h3 className="text-sm font-semibold text-white leading-tight">
                      {displayData.title}
                    </h3>
                    <p className="text-xs text-white/50">
                      {displayData.subtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop: Centered 4-column grid */}
          <div className="hidden md:block">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {usecases.map((usecase) => {
                const displayData = useCaseDisplayData[usecase.id] || {
                  title: usecase.title,
                  subtitle: usecase.persona_label || '',
                  benefit: ''
                };

                return (
                  <button
                    key={usecase.id}
                    onClick={() => handleSelectUsecase(usecase.id)}
                    className="group text-left"
                  >
                    {/* Image - IKEA style thumbnail */}
                    <div className="relative aspect-[1/1] rounded-2xl overflow-hidden mb-4 transition-all">
                      {displayData.thumbnail ? (
                        <img 
                          src={displayData.thumbnail} 
                          alt={displayData.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : usecase.background_image ? (
                        <img 
                          src={usecase.background_image} 
                          alt={displayData.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-emerald-600/30 to-emerald-900/30" />
                      )}
                      {/* Play icon overlay - always visible, brighter on hover */}
                      <div className="absolute inset-0 flex items-center justify-center transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-white/70 group-hover:bg-white flex items-center justify-center shadow-xl transition-all">
                          <Play size={24} className="fill-gray-900 text-gray-900 ml-1 opacity-60 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </div>

                    {/* Text - separate from image like Apple */}
                    <div>
                      {/* Title */}
                      <h3 className="text-base font-semibold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                        <span className="font-bold">{displayData.title}.</span>
                        {' '}
                        <span className="font-normal text-white/60">{displayData.subtitle}</span>
                      </h3>
                      
                      {/* Description */}
                      <p className="text-sm text-white/50 leading-relaxed">
                        {usecase.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Demo mode - with background image container */
        <div className="relative -mx-6 md:mx-0">
          {/* Background - edge to edge on mobile, rounded on desktop */}
          <div className="absolute inset-0 md:rounded-[50px] overflow-hidden">
            {activeUsecase?.background_image && backgroundLoaded && (
              <img 
                key={activeUsecaseId}
                src={activeUsecase.background_image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {/* Dark overlay - gradient to black on left for sidebar */}
            <div className="absolute inset-0 bg-black/50 lg:bg-gradient-to-r lg:from-black lg:via-black/70 lg:to-black/40" />
          </div>

          {/* Content */}
          <div className="relative z-10 py-8 lg:py-12">
            <DemoChatProvider 
              usecase={activeUsecase}
              autoPlay={false}
              playbackSpeed={1.3}
            >
              <div className="relative">
                {/* Desktop: Unified sidebar with intro + chapters */}
                <div className="hidden lg:block absolute left-12 top-8 z-10">
                  {/* Back button */}
                  <button
                    onClick={handleBackToGrid}
                    className="flex items-center gap-1 text-white/50 hover:text-white transition-colors mb-4"
                  >
                    <ChevronLeft size={16} />
                    <span className="text-xs">All use cases</span>
                  </button>
                  
                  {showChapters && (
                    <ChapterNarrative
                      chapters={chapterState.chapters?.length > 0 ? chapterState.chapters : activeUsecase?.chapters || []}
                      chapterIndex={chapterState.chapterIndex}
                      isComplete={chapterState.isComplete}
                      isPaused={chapterState.isAtPause}
                      onContinue={chapterState.onContinue}
                      onJumpToChapter={chapterState.onJumpToChapter}
                      hasStarted={chapterState.isActive}
                      usecase={activeUsecase}
                    />
                  )}
                </div>

                {/* Mobile: Back button + pills on top */}
                <div className="lg:hidden px-6 mb-4">
                  <button
                    onClick={handleBackToGrid}
                    className="flex items-center gap-1 text-white/60 hover:text-white transition-colors mb-3"
                  >
                    <ChevronLeft size={18} />
                    <span className="text-sm">All use cases</span>
                  </button>

                  {/* Small pills for switching */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    {usecases.map((usecase) => {
                      const isActive = usecase.id === activeUsecaseId;
                      const displayData = useCaseDisplayData[usecase.id] || { title: usecase.title };
                      
                      return (
                        <button
                          key={usecase.id}
                          onClick={() => setActiveUsecaseId(usecase.id)}
                          className={`
                            px-3 py-1.5 rounded-full text-xs font-medium transition-all
                            ${isActive 
                              ? 'bg-white text-gray-900' 
                              : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                            }
                          `}
                        >
                          {displayData.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Phone Demo - centered on mobile, right side on desktop */}
                <div className="flex justify-center px-4 sm:px-6 lg:px-0 lg:pl-56">
                  <div className="w-[340px] sm:w-[360px] lg:w-[400px] flex-shrink-0">
                    <PhoneDemoWithOverlay 
                      usecase={activeUsecase}
                      usecaseId={activeUsecaseId}
                      onChapterState={handleChapterState}
                    />
                    
                    {/* Mobile: Chapter narrative below phone */}
                    {showChapters && chapterState.isActive && (
                      <div className="lg:hidden mt-4">
                        <ChapterNarrative
                          chapters={chapterState.chapters?.length > 0 ? chapterState.chapters : activeUsecase?.chapters || []}
                          chapterIndex={chapterState.chapterIndex}
                          isComplete={chapterState.isComplete}
                          isPaused={chapterState.isAtPause}
                          onContinue={chapterState.onContinue}
                          onJumpToChapter={chapterState.onJumpToChapter}
                          hasStarted={chapterState.isActive}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DemoChatProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default UseCaseShowcase;
